import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { createFrontendClient } from '@pipedream/sdk/browser'
import { IntegrationsService, type ConnectedAccount, type ConnectToken, type IntegrationApp } from '../client'
import { apiErrorMessage } from '../lib/api'
import { PLATFORM_ICONS } from '../lib/platformIcons'
import Spinner from '../components/chat/Spinner'

const PRIMARY_BUTTON =
  'inline-flex h-8 items-center justify-center rounded-[10px] bg-linear-to-b from-stone-700 to-stone-950 px-3 text-xs font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity enabled:hover:opacity-90 disabled:opacity-60'
const SECONDARY_BUTTON =
  'inline-flex h-8 items-center justify-center rounded-[10px] border border-stone-300 px-3 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-200 disabled:opacity-60'

const ALL_APPS = 'All apps'
const META_ADS = 'meta_ads'
const META_PATH = PLATFORM_ICONS.find((icon) => icon.match === 'facebook')?.path ?? ''

const AUTH_LABELS: Record<string, string> = { oauth: 'OAuth', keys: 'API key' }

const META_APP: IntegrationApp = {
  slug: META_ADS,
  name: 'Meta Ads',
  description: 'Create, publish and analyse ads across Facebook and Instagram from adlovin.',
  icon: null,
  auth_type: 'oauth',
  category: 'Marketing & Ads',
  actions: 0,
}

function withMeta(apps: IntegrationApp[]) {
  const at = apps.findIndex((app) => app.slug === 'google_ads')
  if (at < 0) return [...apps, META_APP]
  return [...apps.slice(0, at + 1), META_APP, ...apps.slice(at + 1)]
}

function sdkToken(token: ConnectToken) {
  return {
    token: token.token,
    expiresAt: new Date(token.expires_at),
    connectLinkUrl: token.connect_link_url,
  }
}

function matches(app: IntegrationApp, query: string) {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return [app.name, app.description, app.category].some((text) => text.toLowerCase().includes(needle))
}

function AppIcon({ app }: { app: IntegrationApp }) {
  return (
    <span className="flex size-11 items-center justify-center rounded-xl border border-stone-300">
      {app.slug === META_ADS ? (
        <svg viewBox="0 0 24 24" className="size-6 fill-[#0866FF]" aria-hidden="true">
          <path d={META_PATH} />
        </svg>
      ) : (
        app.icon && <img src={app.icon} alt="" className="size-6 object-contain" />
      )}
    </span>
  )
}

export default function Integrations() {
  const [apps, setApps] = useState<IntegrationApp[]>([])
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState(ALL_APPS)

  const refresh = useCallback(
    () =>
      IntegrationsService.listConnectedAccounts()
        .then(setAccounts)
        .catch((err: unknown) => setError(apiErrorMessage(err))),
    [],
  )

  useEffect(() => {
    Promise.all([IntegrationsService.listApps().then((list) => setApps(withMeta(list))), refresh()])
      .catch((err: unknown) => setError(apiErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [refresh])

  const categories = useMemo(() => [ALL_APPS, ...new Set(apps.map((app) => app.category))], [apps])
  const shown = useMemo(
    () => apps.filter((app) => (category === ALL_APPS || app.category === category) && matches(app, query)),
    [apps, category, query],
  )

  async function connect(app: string) {
    setBusy(app)
    setError(null)
    try {
      const first = await IntegrationsService.createConnectToken()
      const client = createFrontendClient({
        externalUserId: first.external_user_id,
        tokenCallback: async () => sdkToken(await IntegrationsService.createConnectToken()),
      })
      await client.connectAccount({
        app,
        token: first.token,
        onSuccess: () => void refresh(),
        onError: (err) => setError(err.message),
        onClose: () => setBusy(null),
      })
    } catch (err) {
      setError(apiErrorMessage(err))
      setBusy(null)
    }
  }

  async function disconnect(id: string) {
    setBusy(id)
    setError(null)
    try {
      await IntegrationsService.disconnectAccount(id)
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-8 md:px-6">
      <div className="mx-auto max-w-6xl">
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Integrations</h1>
        <p className="mt-2 text-sm text-stone-500">
          Connect the tools you already use. Connections are shared by every brand on your account.
        </p>

        {error && <p className="mt-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

        {loading ? (
          <Spinner className="mt-8 size-4 border-stone-300 border-t-stone-500" />
        ) : (
          <div className="mt-8 flex flex-col gap-6 md:flex-row md:gap-10">
            <nav className="md:w-48 md:shrink-0">
              <ul className="-mx-4 flex gap-1 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:flex-col md:px-0">
                {categories.map((name) => (
                  <li key={name} className="shrink-0">
                    <button
                      type="button"
                      onClick={() => setCategory(name)}
                      className={`w-full rounded-lg px-3 py-1.5 text-left text-sm whitespace-nowrap transition-colors md:rounded-none md:border-l-2 md:py-2 ${
                        category === name
                          ? 'bg-purple-50 font-medium text-purple-900 md:border-purple-700 md:bg-transparent'
                          : 'text-stone-500 hover:text-stone-900 md:border-transparent'
                      }`}
                    >
                      {name}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="min-w-0 flex-1">
              <label htmlFor="app-search" className="text-xs font-semibold tracking-wide text-stone-500 uppercase">
                Search apps
              </label>
              <div className="relative mt-2">
                <MagnifyingGlassIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-stone-400" />
                <input
                  id="app-search"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Try “Slack” or “CRM”"
                  className="w-full min-w-0 rounded-lg border border-stone-300 bg-white py-2.5 pr-3 pl-9 text-sm text-stone-900 placeholder:text-stone-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 focus:outline-none"
                />
              </div>

              <p className="mt-6 text-sm text-stone-500">
                {shown.length} {shown.length === 1 ? 'app' : 'apps'}
              </p>

              {shown.length === 0 ? (
                <p className="mt-4 rounded-2xl border border-dashed border-stone-300 px-4 py-10 text-center text-sm text-stone-500">
                  No apps match “{query.trim()}”.
                </p>
              ) : (
                <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {shown.map((app) => {
                    const connected = accounts.filter((account) => account.app === app.slug)
                    return (
                      <li
                        key={app.slug}
                        className="flex flex-col rounded-2xl border border-stone-300 p-5 transition-colors hover:border-stone-400"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <AppIcon app={app} />
                          <span className="rounded-full bg-stone-200 px-2.5 py-0.5 text-xs text-stone-600">
                            {AUTH_LABELS[app.auth_type] ?? 'No sign-in'}
                          </span>
                        </div>

                        <p className="mt-4 font-heading text-base font-semibold text-stone-900">{app.name}</p>
                        <p className="mt-1 line-clamp-2 text-sm text-stone-500">{app.description}</p>

                        {connected.length > 0 && (
                          <ul className="mt-4 space-y-1.5 border-t border-stone-200 pt-3">
                            {connected.map((account) => (
                              <li key={account.id} className="flex items-center justify-between gap-3 text-xs">
                                <span className="flex min-w-0 items-center gap-1.5 text-stone-700">
                                  <span
                                    className={`size-1.5 shrink-0 rounded-full ${account.healthy ? 'bg-purple-600' : 'bg-amber-500'}`}
                                  />
                                  <span className="truncate">{account.name ?? 'Connected account'}</span>
                                  {!account.healthy && <span className="shrink-0 text-amber-600">Reconnect</span>}
                                </span>
                                <button
                                  type="button"
                                  disabled={busy !== null}
                                  onClick={() => void disconnect(account.id)}
                                  className="shrink-0 text-stone-500 transition-colors enabled:hover:text-red-600 disabled:opacity-60"
                                >
                                  {busy === account.id ? 'Removing…' : 'Disconnect'}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}

                        <div className="mt-auto flex items-center justify-between gap-3 pt-4">
                          <span className="text-xs text-stone-500">
                            {app.slug === META_ADS ? 'Built into adlovin' : `${app.actions} actions`}
                          </span>
                          {app.slug === META_ADS ? (
                            <Link to="/my-ads" className={SECONDARY_BUTTON}>
                              Open My Ads
                            </Link>
                          ) : (
                            <button
                              type="button"
                              disabled={busy !== null}
                              onClick={() => void connect(app.slug)}
                              className={connected.length > 0 ? SECONDARY_BUTTON : PRIMARY_BUTTON}
                            >
                              {busy === app.slug ? 'Connecting…' : connected.length > 0 ? 'Add account' : 'Connect'}
                            </button>
                          )}
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
